import * as React from "react";
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { EmptyTableRow } from "./empty-state";
import { ErrorState } from "./error-state";
import { TableSkeleton } from "./loading-skeleton";

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[] | undefined;
  isLoading?: boolean;
  isError?: boolean;
  error?: { message?: string } | null;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  skeletonRows?: number;
  onRowClick?: (row: TData) => void;
  className?: string;
}

/**
 * Unifies the loading-skeleton / error / empty / populated table pattern
 * copy-pasted across employees, categories, sub-categories, car-services,
 * transactions, orders and users pages. Column definitions stay
 * feature-specific (pass a *-table-columns.tsx ColumnDef[] in), matching the
 * shape already used by the companies feature.
 */
export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  isError = false,
  error,
  onRetry,
  emptyTitle = "No results.",
  emptyDescription,
  skeletonRows = 4,
  onRowClick,
  className,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
  });

  if (isLoading) {
    return (
      <div className={cn("rounded-md border bg-card", className)}>
        <TableSkeleton rows={skeletonRows} columns={columns.length} />
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={error?.message || "Failed to load data"} onRetry={onRetry} />;
  }

  return (
    <ScrollArea className={cn("rounded-md border bg-card", className)}>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                className={cn(onRowClick && "cursor-pointer")}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <EmptyTableRow
              colSpan={columns.length}
              title={emptyTitle}
              description={emptyDescription}
            />
          )}
        </TableBody>
      </Table>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
